import { TabStyles, TextLinkWidget, Tooltip } from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';
import { style } from 'typestyle';

import { imgPath } from '~common/utilities/ImgUrl';
import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~commonInfra/services/HelpService';
import { SessionServerSelectors,SessionSiteSelectors, SessionUserSelectors } from '~selectors/SessionInfoSelectors';
import { DisableLinkedTaskDialogWarning } from '~src/linkedTasks/components/DisableLinkedTaskDialogWarning';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

export type LinkedTaskSiteSettingSectionProps = SiteSettingsForSiteAdminProps<'linkedTaskSchedulingEnabled' | 'linkedTaskRunNowEnabled'>;

export const LINKEDTASKS_SITE_SETTINGS_TESTID = 'linked-tasks-site-settings-test-id';
export const LINKEDTASKS_SITE_SETTINGS_HELP_TESTID = 'linked-tasks-site-settings-help-test-id';
export const LINKEDTASKS_SITE_SETTINGS_SCHEDULING_CHECKBOX_TESTID = 'linked-tasks-on-site-checkbox-test-id';
export const LINKEDTASKS_SITE_SETTINGS_RUNNOW_CHECKBOX_TESTID = 'linked-tasks-run-now-on-site-checkbox-test-id';
export const LINKEDTASKS_SITE_SETTINGS_RUNNOW_TOOLTIP_TESTID = 'linked-task-setting-run-now-tooltip-test-id';
export const LINKEDTASKS_DISABLED_ON_SITE_TOOLTIP_TESTID = 'linked-task-disabled-on-site-tooltip-test-id';
const LINKEDTASKS_SCHEDULING_ENABLED = 'settings.linkedTaskSchedulingEnabled';
const LINKEDTASKS_RUNNOW_ENABLED = 'settings.linkedTaskRunNowEnabled';

export function LinkedTaskSiteSettingSection(props: LinkedTaskSiteSettingSectionProps) {
  const isOnline = useSelector(SessionServerSelectors.isOnlineEnvironment);
  const linkedTaskSchedulingEnabledOnServer = useSelector(SessionServerSelectors.linkedTasksSchedulingEnabled);
  const runNowEnabled = useSelector(SessionSiteSelectors.isRunNowEnabledOnSite);
  const formik = useFormikContext<LinkedTaskSiteSettingSectionProps>();
  return (useShowSettingsSection()) ?
    <SettingsSection
      title={Messages.linkedTasks()}
      testId={LINKEDTASKS_SITE_SETTINGS_TESTID}
      description={
        <div>
          {Messages.linkedTasksSiteSettings_description1()}
          <TextLinkWidget
            testId={LINKEDTASKS_SITE_SETTINGS_HELP_TESTID}
            target={'_blank'}
            className={learnMoreStyle}
            href={HelpService.getServerHelpUrlByCategory('prep-conductor-linked-tasks')}>
            {Messages.learnMore()}
          </TextLinkWidget>
        </div>
      }>
      <div className={SettingsStyles.checkboxGroup}>
        {LinkedTaskSchedulingCheckbox(formik, isOnline || linkedTaskSchedulingEnabledOnServer)}
        {LinkedTaskRunNowCheckbox(formik, runNowEnabled)}
      </div>
    </SettingsSection> : null;
}

function LinkedTaskSchedulingCheckbox(formik: FormikContextType<LinkedTaskSiteSettingSectionProps>, linkedTaskSchedulingEnabledOnSite: boolean): JSX.Element {
  const tooltipStyle = {
    display: 'flex',
    alignItems: 'center',
  };
  if (linkedTaskSchedulingEnabledOnSite) {
    return (
      <div className={SettingsStyles.checkboxGroup}>
        <FormikCheckbox
          name={LINKEDTASKS_SCHEDULING_ENABLED}
          label={Messages.linkedTasksSiteSettingsEnable()}
          testId={LINKEDTASKS_SITE_SETTINGS_SCHEDULING_CHECKBOX_TESTID}
          beforeChange={formik.values.settings.linkedTaskSchedulingEnabled ? openConfirmationDialog : undefined}
          disabled={!linkedTaskSchedulingEnabledOnSite}
        />
      </div>
    );
  } else {
    return (
      <div className={style(tooltipStyle)}>
        <FormikCheckbox
          name={LINKEDTASKS_SCHEDULING_ENABLED}
          label={Messages.linkedTasksSiteSettingsEnable()}
          testId={LINKEDTASKS_SITE_SETTINGS_SCHEDULING_CHECKBOX_TESTID}
          disabled={!linkedTaskSchedulingEnabledOnSite}
        />
        <Tooltip
          testId={LINKEDTASKS_DISABLED_ON_SITE_TOOLTIP_TESTID}
          getContent={() => { return <span>{Messages.linkedTasksDisabledOnSite()}</span>; }}
        >
          <img className={style({ paddingLeft: TabStyles.Spacing.SmallSpace })}
            data-tb-test-id={LINKEDTASKS_DISABLED_ON_SITE_TOOLTIP_TESTID}
            src={imgPath('data_prep_info.svg')}
            alt={Messages.linkedTasksDisabledOnSite()} />
        </Tooltip>
      </div>
    );
  }
}

function openConfirmationDialog(event: React.ChangeEvent<HTMLInputElement>) {
  const dialogPromiseResult = openReactConfirmationDialog({
    title: Messages.linkedTasksDisable(),
    message: Messages.linkedTasksDisableOnSiteConfirmationText(),
    actionName:  Messages.linkedTasksDisableDialogSubmit(),
    action: () => Promise.resolve(),
    bodyComponent: DisableLinkedTaskDialogWarning,
    dialogWidth: TabDialogWidth.width375px,
    paramsForAction: null,
  }).result;

  return dialogPromiseResult;
}

export function LinkedTaskRunNowCheckbox(formik: FormikContextType<LinkedTaskSiteSettingSectionProps>, isRunNowEnabled: boolean): JSX.Element {
  const tooltipStyle = {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: TabStyles.Spacing.LargeSpace,
  };
  const isLinkedTaskSchedulingEnabled = formik.values.settings.linkedTaskSchedulingEnabled;
  if (isLinkedTaskSchedulingEnabled) {
    if (!isRunNowEnabled) {
      return (
        <div className={style(tooltipStyle)}>
          <FormikCheckbox
            name={LINKEDTASKS_RUNNOW_ENABLED}
            label={Messages.linkedTasksSiteSettingsEnableRunNow()}
            testId={LINKEDTASKS_SITE_SETTINGS_RUNNOW_CHECKBOX_TESTID}
            disabled={!isRunNowEnabled}
          />
          <Tooltip
            testId={LINKEDTASKS_SITE_SETTINGS_RUNNOW_TOOLTIP_TESTID}
            getContent={() => { return <span>{Messages.linkedTasksSiteSettingsEnableRunNowTooltip()}</span>; }}
          >
            <img className={style({ paddingLeft: TabStyles.Spacing.SmallSpace })}
              data-tb-test-id={LINKEDTASKS_SITE_SETTINGS_RUNNOW_TOOLTIP_TESTID}
              src={imgPath('data_prep_info.svg')}
              alt={Messages.linkedTasksSiteSettingsEnableRunNowTooltip()} />
          </Tooltip>
        </div>
      );
    } else {
      return (
        <div className={SettingsStyles.settingsSubGroup}>
          <FormikCheckbox
            name={LINKEDTASKS_RUNNOW_ENABLED}
            label={Messages.linkedTasksSiteSettingsEnableRunNow()}
            testId={LINKEDTASKS_SITE_SETTINGS_RUNNOW_CHECKBOX_TESTID}
            disabled={!isRunNowEnabled}
          />
        </div>
      );
    }
  }
  return (
    <div className={SettingsStyles.settingsSubGroup}>
      <FormikCheckbox
        name={LINKEDTASKS_RUNNOW_ENABLED}
        label={Messages.linkedTasksSiteSettingsEnableRunNow()}
        testId={LINKEDTASKS_SITE_SETTINGS_RUNNOW_CHECKBOX_TESTID}
        disabled={!isLinkedTaskSchedulingEnabled}
      />
    </div>
  );
}

function useShowSettingsSection(): boolean {
  const isServerAdmin = useSelector(SessionUserSelectors.isSessionServerAdmin);
  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
  const isSiteAdmin = useSelector(SessionSiteSelectors.isSessionSiteAdmin);

  const isAllowedToChangeSetting = isServerAdmin || (isOnlineEnvironment && isSiteAdmin);
  const prepConductorEnabled = useSelector(SessionSiteSelectors.isFlowOnSiteEnabled);
  return FeatureFlags.LinkedTaskSchedulingEnabled && isAllowedToChangeSetting && prepConductorEnabled;
}

/* for registering the component in settings registry */
export const LinkedTaskSiteSettingSpecs = componentRegistryEntry({
  name: 'LinkedTaskSiteSettingSection',
  component: LinkedTaskSiteSettingSection
});